// pages/admin/EditProductPage.tsx
import { ErrorMessage, Field, FieldArray, Form, Formik } from "formik";
import React, { useEffect, useState } from "react";
import Dropzone from "react-dropzone";
import { FaPlus, FaTrash } from "react-icons/fa";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Collapse,
  FormGroup,
  Input,
  Label,
  Row,
  Spinner,
  Tooltip,
} from "reactstrap";
import * as Yup from "yup";
import { getAllCategories } from "../../../../../api/categoryAPIs";
import { getProductById, updateProduct } from "../../../../../api/productAPIs";
import { getAllSubCategories } from "../../../../../api/subCategoryAPIs";
import { PreviewCard } from "../../../../../components/Component";
import Content from "../../../../../layout/content/Content";

const EditProductPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [existingImages, setExistingImages] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [existingVideo, setExistingVideo] = useState(null);
  const [newVideo, setNewVideo] = useState(null);
  const [variantOpen, setVariantOpen] = useState([]);
  const [tooltipOpen, setTooltipOpen] = useState({});

  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link", "image"],
      ["clean"],
    ],
  };

  const formats = ["header", "bold", "italic", "underline", "strike", "list", "bullet", "link", "image"];

  useEffect(() => {
    const fetchProductDetails = async () => {
      try {
        const productResponse = await getProductById({ id: productId });
        const productData = productResponse.data.data;
        setProduct(productData);
        setExistingImages(productData.images.map(img => img.url) || []);
        setExistingVideo(productData.video || null);

        if (productData.category_id) {
          const subCategoriesResponse = await getAllSubCategories({ parentCategoryId: productData.category_id });
          setSubCategories(subCategoriesResponse.data.data);
        }
        setVariantOpen(new Array(productData.variants.length).fill(true));
      } catch (error) {
        console.error("Error fetching product details:", error);
        toast.error("Failed to load product data", { position: "top-center" });
      } finally {
        setInitialLoad(false);
      }
    };

    const fetchAllCategories = async () => {
      try {
        const categoriesResponse = await getAllCategories();
        setCategories(categoriesResponse.data.data);
      } catch (error) {
        console.error("Error fetching categories:", error);
        toast.error("Failed to load categories", { position: "top-center" });
      }
    };

    fetchProductDetails();
    fetchAllCategories();
  }, [productId]);

  const handleCategoryChange = async (categoryId, setFieldValue) => {
    try {
      const response = await getAllSubCategories({ parentCategoryId: categoryId });
      setSubCategories(response.data.data);
      setFieldValue("subcategory_id", "");
    } catch (error) {
      console.error("Error fetching subcategories:", error);
      toast.error("Failed to load subcategories", { position: "top-center" });
    }
  };

  const handleSubCategoryChange = (subcategoryId, setFieldValue) => {
    setFieldValue("subcategory_id", subcategoryId);
  };

  const handleImageDropChange = (acceptedFiles, setFieldValue) => {
    const newFiles = acceptedFiles.map((file, index) =>
      Object.assign(file, {
        preview: URL.createObjectURL(file),
        sequence: existingImages.length + newImages.length + index
      })
    );
    setNewImages((prev) => [...prev, ...newFiles]);
    setFieldValue("images", [...existingImages, ...newImages, ...newFiles]);
  };

  const handleVideoDropChange = (acceptedFiles, setFieldValue) => {
    const video = acceptedFiles[0];
    if (video) {
      setNewVideo(Object.assign(video, { preview: URL.createObjectURL(video) }));
      setFieldValue("video", video);
    }
  };

  const handleRemoveImage = (index, isExisting, setFieldValue) => {
    if (isExisting) {
      const updatedExistingImages = existingImages.filter((_, i) => i !== index);
      setExistingImages(updatedExistingImages);
    } else {
      const updatedNewImages = newImages.filter((_, i) => i !== index);
      setNewImages(updatedNewImages);
    }
    setFieldValue("images", [...existingImages, ...newImages]);
  };

  const handleRemoveVideo = (setFieldValue) => {
    setExistingVideo(null);
    setNewVideo(null);
    setFieldValue("video", null);
  };

  const generateSKU = (name, variantName) => {
    if (!name || !variantName) return "";
    return `${name}-${variantName}`
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .substring(0, 50);
  };

  const initialValues = product
    ? {
      name: product.name || "",
      description: product.description || "",
      category_id: product.category_id || "",
      subcategory_id: product.subcategory_id || "",
      brand: product.brand || "",
      images: product.images.map(img => img.url) || [],
      video: product.video || null,
      isBestSeller: product.isBestSeller || false,
      isSeasonalFavorite: product.isSeasonalFavorite || false,
      isNewLaunch: product.isNewLaunch || false,
      variants: product.variants.map((variant) => ({
        name: variant.name || "",
        price: variant.price?.$numberDecimal || "",
        stock: variant.stock || 0,
        weight: variant.weight || "",
        dimensions: {
          height: variant.dimensions?.height || "",
          length: variant.dimensions?.length || "",
          width: variant.dimensions?.width || "",
        },
        sku: variant.sku || generateSKU(product.name, variant.name),
        discount: variant.discount || {
          type: "",
          value: "",
          active: false,
          startDate: "",
          endDate: ""
        },
        images: variant.images || [],
      })),
    }
    : {};

  const variantSchema = Yup.object({
    name: Yup.string().required("Variant name is required"),
    price: Yup.number().required("Price is required").positive("Price must be positive"),
    stock: Yup.number()
      .required("Stock is required")
      .integer("Stock must be an integer")
      .min(0, "Stock cannot be negative"),
    weight: Yup.number().required("Weight is required").positive("Weight must be positive"),
    dimensions: Yup.object({
      height: Yup.number().required("Height is required").positive("Height must be positive"),
      length: Yup.number().required("Length is required").positive("Length must be positive"),
      width: Yup.number().required("Width is required").positive("Width must be positive"),
    }),
    sku: Yup.string().required("SKU is required"),
    images: Yup.array().of(Yup.string()),
    discount: Yup.object({
      type: Yup.string().oneOf(["percentage", "flat", ""]).optional(),
      value: Yup.number().when("type", (type, schema) =>
        type === "percentage" || type === "flat"
          ? schema
            .required("Discount value is required when type is selected")
            .positive("Discount must be positive")
            .test(
              "max-percentage",
              "Percentage cannot exceed 100",
              (value) => type !== "percentage" || value <= 100
            )
          : schema.nullable()
      ),
      active: Yup.boolean(),
      startDate: Yup.date().nullable(),
      endDate: Yup.date().nullable(),
    }),
  });

  const validationSchema = Yup.object({
    name: Yup.string().required("Product name is required"),
    description: Yup.string()
      .required("Description is required")
      .test("is-empty", "Description is required", (value) => value.replace(/<[^>]+>/g, "").trim().length > 0),
    category_id: Yup.string().required("Category is required"),
    subcategory_id: Yup.string().required("Subcategory is required"),
    brand: Yup.string().required("Brand is required"),
    images: Yup.array()
      .min(1, "At least one image is required")
      .test(
        "fileType",
        "Only .jpeg, .png, .jpg accepted",
        (files) =>
          files && files.every((file) => typeof file === "string" || ["image/jpeg", "image/png", "image/jpg"].includes(file.type))
      ),
    video: Yup.mixed()
      .nullable()
      .test("fileType", "Only MP4 accepted", (file) => !file || (typeof file === "string" ? true : file.type === "video/mp4")),
    isBestSeller: Yup.boolean(),
    isSeasonalFavorite: Yup.boolean(),
    isNewLaunch: Yup.boolean(),
    variants: Yup.array().of(variantSchema).min(1, "At least one variant is required"),
  });

  const handleSubmit = async (values) => {
    if (!existingImages.length && !newImages.length) {
      toast.error("Please upload at least one image", { position: "top-center" });
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();

      const updatedVariants = values.variants.map((variant) => ({
        ...variant,
        discount: variant.discount.type ? {
          ...variant.discount,
          value: variant.discount.value || 0,
          startDate: variant.discount.startDate || undefined,
          endDate: variant.discount.endDate || undefined
        } : undefined
      }));

      const { images, video, ...valuesWithoutFiles } = { ...values, variants: updatedVariants };
      const productData = {
        ...valuesWithoutFiles,
        oldImages: existingImages
      };

      formData.append("data", JSON.stringify(productData));

      // Append new images with their sequence
      newImages.forEach((file, index) => {
        formData.append("images", file);
      });

      formData.append("video", newVideo ? newVideo : "");


      const response = await updateProduct({ productId, formData });

      if (response.error) {
        toast.error(response.error, { position: "top-center" });
      } else {
        toast.success("Product updated successfully", { position: "top-center" });
        // navigate("/products");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Something went wrong.", { position: "top-center" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      newImages.forEach((file) => URL.revokeObjectURL(file.preview));
      if (newVideo) URL.revokeObjectURL(newVideo.preview);
    };
  }, [newImages, newVideo]);

  if (initialLoad) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
        <Spinner color="primary" />
      </div>
    );
  }

  return (
    <Content>
      <div className="container my-4">
        <PreviewCard className="shadow-sm">
          <CardHeader className="bg-primary text-white">
            <h3 className="mb-0">Edit Product</h3>
          </CardHeader>
          <CardBody>
            {product ? (
              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                enableReinitialize
              >
                {({ setFieldValue, values, errors, touched }) => (
                  <Form>
                    {/* Product Details Section */}
                    <Card className="mb-4 border-0 shadow-sm">
                      <CardHeader className="bg-light">
                        <h5 className="mb-0">Product Details</h5>
                      </CardHeader>
                      <CardBody>
                        <Row className="g-4">
                          <Col md="6">
                            <FormGroup>
                              <Label for="name">
                                Product Name{" "}
                                <span id="nameTooltip" className="text-muted small">
                                  (required)
                                </span>
                              </Label>
                              <Field
                                as={Input}
                                name="name"
                                id="name"
                                placeholder="Enter product name"
                                invalid={touched.name && !!errors.name}
                              />
                              <Tooltip
                                placement="top"
                                isOpen={tooltipOpen.name}
                                target="nameTooltip"
                                toggle={() => setTooltipOpen((prev) => ({ ...prev, name: !prev.name }))}
                              >
                                Enter a unique product name
                              </Tooltip>
                              <ErrorMessage name="name" component="div" className="invalid-feedback" />
                            </FormGroup>
                          </Col>
                          <Col md="6">
                            <FormGroup>
                              <Label for="brand">Brand</Label>
                              <Field
                                as={Input}
                                name="brand"
                                id="brand"
                                placeholder="Enter brand name"
                                invalid={touched.brand && !!errors.brand}
                              />
                              <ErrorMessage name="brand" component="div" className="invalid-feedback" />
                            </FormGroup>
                          </Col>
                          <Col md="6">
                            <FormGroup>
                              <Label for="category_id">Category</Label>
                              <Field
                                as={Input}
                                type="select"
                                name="category_id"
                                id="category_id"
                                invalid={touched.category_id && !!errors.category_id}
                                onChange={(e) => {
                                  setFieldValue("category_id", e.target.value);
                                  handleCategoryChange(e.target.value, setFieldValue);
                                }}
                              >
                                <option value="">Select Category</option>
                                {categories.map((category) => (
                                  <option key={category._id} value={category._id}>
                                    {category.name}
                                  </option>
                                ))}
                              </Field>
                              <ErrorMessage name="category_id" component="div" className="invalid-feedback" />
                            </FormGroup>
                          </Col>
                          <Col md="6">
                            <FormGroup>
                              <Label for="subcategory_id">Subcategory</Label>
                              <Field
                                as={Input}
                                type="select"
                                name="subcategory_id"
                                id="subcategory_id"
                                invalid={touched.subcategory_id && !!errors.subcategory_id}
                                onChange={(e) => handleSubCategoryChange(e.target.value, setFieldValue)}
                              >
                                <option value="">Select Subcategory</option>
                                {subCategories.map((subcategory) => (
                                  <option key={subcategory._id} value={subcategory._id}>
                                    {subcategory.name}
                                  </option>
                                ))}
                              </Field>
                              <ErrorMessage name="subcategory_id" component="div" className="invalid-feedback" />
                            </FormGroup>
                          </Col>
                          <Col md="12">
                            <FormGroup>
                              <Label for="description">Description</Label>
                              <ReactQuill
                                theme="snow"
                                value={values.description}
                                onChange={(value) => setFieldValue("description", value)}
                                modules={modules}
                                formats={formats}
                                placeholder="Enter product description"
                                className={touched.description && errors.description ? "is-invalid" : ""}
                              />
                              <ErrorMessage name="description" component="div" className="invalid-feedback d-block" />
                            </FormGroup>
                          </Col>
                          <Col md="12">
                            <FormGroup check className="d-inline-block me-4">
                              <Field
                                type="checkbox"
                                name="isBestSeller"
                                id="isBestSeller"
                                as={Input}
                              />
                              <Label check for="isBestSeller" className="ms-2">Mark as Best Seller</Label>
                            </FormGroup>
                            <FormGroup check className="d-inline-block me-4">
                              <Field
                                type="checkbox"
                                name="isSeasonalFavorite"
                                id="isSeasonalFavorite"
                                as={Input}
                              />
                              <Label check for="isSeasonalFavorite" className="ms-2">Mark as Seasonal Favorite</Label>
                            </FormGroup>
                            <FormGroup check className="d-inline-block">
                              <Field
                                type="checkbox"
                                name="isNewLaunch"
                                id="isNewLaunch"
                                as={Input}
                              />
                              <Label check for="isNewLaunch" className="ms-2">Mark as New Launch</Label>
                            </FormGroup>
                          </Col>
                        </Row>
                      </CardBody>
                    </Card>

                    {/* Variants Section */}
                    <Card className="mb-4 border-0 shadow-sm">
                      <CardHeader className="bg-light">
                        <h5 className="mb-0">Variants</h5>
                      </CardHeader>
                      <CardBody>
                        <FieldArray name="variants">
                          {({ push, remove }) => (
                            <div>
                              {values.variants && values.variants.length > 0 ? (
                                values.variants.map((variant, index) => (
                                  <Card key={index} className="mb-3 border-0 shadow-sm">
                                    <CardHeader
                                      className="bg-secondary text-white cursor-pointer"
                                      onClick={() =>
                                        setVariantOpen((prev) => {
                                          const newState = [...prev];
                                          newState[index] = !newState[index];
                                          return newState;
                                        })
                                      }
                                    >
                                      <h6 className="mb-0">
                                        Variant {index + 1}: {variant.name || "Unnamed"}
                                      </h6>
                                    </CardHeader>
                                    <Collapse isOpen={variantOpen[index] !== false}>
                                      <CardBody>
                                        <Row className="g-4">
                                          <Col md="6">
                                            <FormGroup>
                                              <Label for={`variants[${index}].name`}>Variant Name</Label>
                                              <Field
                                                as={Input}
                                                name={`variants[${index}].name`}
                                                id={`variants[${index}].name`}
                                                placeholder="e.g., 1kg, 400ml"
                                                invalid={
                                                  touched.variants?.[index]?.name && !!errors.variants?.[index]?.name
                                                }
                                                onChange={(e) => {
                                                  setFieldValue(`variants[${index}].name`, e.target.value);
                                                  setFieldValue(
                                                    `variants[${index}].sku`,
                                                    generateSKU(values.name, e.target.value)
                                                  );
                                                }}
                                              />
                                              <ErrorMessage
                                                name={`variants[${index}].name`}
                                                component="div"
                                                className="invalid-feedback"
                                              />
                                            </FormGroup>
                                          </Col>
                                          <Col md="6">
                                            <FormGroup>
                                              <Label for={`variants[${index}].price`}>Price ($)</Label>
                                              <Field
                                                as={Input}
                                                type="number"
                                                name={`variants[${index}].price`}
                                                id={`variants[${index}].price`}
                                                placeholder="Enter price"
                                                invalid={
                                                  touched.variants?.[index]?.price && !!errors.variants?.[index]?.price
                                                }
                                              />
                                              <ErrorMessage
                                                name={`variants[${index}].price`}
                                                component="div"
                                                className="invalid-feedback"
                                              />
                                            </FormGroup>
                                          </Col>
                                          <Col md="6">
                                            <FormGroup>
                                              <Label for={`variants[${index}].stock`}>Stock</Label>
                                              <Field
                                                as={Input}
                                                type="number"
                                                name={`variants[${index}].stock`}
                                                id={`variants[${index}].stock`}
                                                placeholder="Enter stock quantity"
                                                invalid={
                                                  touched.variants?.[index]?.stock && !!errors.variants?.[index]?.stock
                                                }
                                              />
                                              <ErrorMessage
                                                name={`variants[${index}].stock`}
                                                component="div"
                                                className="invalid-feedback"
                                              />
                                            </FormGroup>
                                          </Col>
                                          <Col md="6">
                                            <FormGroup>
                                              <Label for={`variants[${index}].weight`}>Weight (kg)</Label>
                                              <Field
                                                as={Input}
                                                type="number"
                                                name={`variants[${index}].weight`}
                                                id={`variants[${index}].weight`}
                                                placeholder="Enter weight"
                                                invalid={
                                                  touched.variants?.[index]?.weight &&
                                                  !!errors.variants?.[index]?.weight
                                                }
                                              />
                                              <ErrorMessage
                                                name={`variants[${index}].weight`}
                                                component="div"
                                                className="invalid-feedback"
                                              />
                                            </FormGroup>
                                          </Col>
                                          <Col md="12">
                                            <Label>Dimensions (cm)</Label>
                                            <Row className="g-3">
                                              <Col sm="4">
                                                <FormGroup>
                                                  <Field
                                                    as={Input}
                                                    type="number"
                                                    name={`variants[${index}].dimensions.height`}
                                                    placeholder="Height"
                                                    invalid={
                                                      touched.variants?.[index]?.dimensions?.height &&
                                                      !!errors.variants?.[index]?.dimensions?.height
                                                    }
                                                  />
                                                  <ErrorMessage
                                                    name={`variants[${index}].dimensions.height`}
                                                    component="div"
                                                    className="invalid-feedback"
                                                  />
                                                </FormGroup>
                                              </Col>
                                              <Col sm="4">
                                                <FormGroup>
                                                  <Field
                                                    as={Input}
                                                    type="number"
                                                    name={`variants[${index}].dimensions.length`}
                                                    placeholder="Length"
                                                    invalid={
                                                      touched.variants?.[index]?.dimensions?.length &&
                                                      !!errors.variants?.[index]?.dimensions?.length
                                                    }
                                                  />
                                                  <ErrorMessage
                                                    name={`variants[${index}].dimensions.length`}
                                                    component="div"
                                                    className="invalid-feedback"
                                                  />
                                                </FormGroup>
                                              </Col>
                                              <Col sm="4">
                                                <FormGroup>
                                                  <Field
                                                    as={Input}
                                                    type="number"
                                                    name={`variants[${index}].dimensions.width`}
                                                    placeholder="Width"
                                                    invalid={
                                                      touched.variants?.[index]?.dimensions?.width &&
                                                      !!errors.variants?.[index]?.dimensions?.width
                                                    }
                                                  />
                                                  <ErrorMessage
                                                    name={`variants[${index}].dimensions.width`}
                                                    component="div"
                                                    className="invalid-feedback"
                                                  />
                                                </FormGroup>
                                              </Col>
                                            </Row>
                                          </Col>
                                          <Col md="12">
                                            <FormGroup>
                                              <Label for={`variants[${index}].sku`}>SKU</Label>
                                              <Field
                                                as={Input}
                                                name={`variants[${index}].sku`}
                                                id={`variants[${index}].sku`}
                                                placeholder="Auto-generated"
                                                readOnly
                                                invalid={
                                                  touched.variants?.[index]?.sku && !!errors.variants?.[index]?.sku
                                                }
                                              />
                                              <ErrorMessage
                                                name={`variants[${index}].sku`}
                                                component="div"
                                                className="invalid-feedback"
                                              />
                                            </FormGroup>
                                          </Col>
                                          {/* Discount Fields */}
                                          <Col md="12">
                                            <Card className="border-0 shadow-sm">
                                              <CardHeader className="bg-light">
                                                <h6 className="mb-0">Discount (Optional)</h6>
                                              </CardHeader>
                                              <CardBody>
                                                <Row className="g-4">
                                                  <Col md="4">
                                                    <FormGroup>
                                                      <Label for={`variants[${index}].discount.type`}>Discount Type</Label>
                                                      <Field
                                                        as={Input}
                                                        type="select"
                                                        name={`variants[${index}].discount.type`}
                                                        invalid={touched.variants?.[index]?.discount?.type && !!errors.variants?.[index]?.discount?.type}
                                                      >
                                                        <option value="">None</option>
                                                        <option value="percentage">Percentage</option>
                                                        <option value="flat">Flat</option>
                                                      </Field>
                                                      <ErrorMessage
                                                        name={`variants[${index}].discount.type`}
                                                        component="div"
                                                        className="invalid-feedback"
                                                      />
                                                    </FormGroup>
                                                  </Col>
                                                  <Col md="4">
                                                    <FormGroup>
                                                      <Label for={`variants[${index}].discount.value`}>Value</Label>
                                                      <Field
                                                        as={Input}
                                                        type="number"
                                                        name={`variants[${index}].discount.value`}
                                                        placeholder="Enter discount value"
                                                        invalid={touched.variants?.[index]?.discount?.value && !!errors.variants?.[index]?.discount?.value}
                                                      />
                                                      <ErrorMessage
                                                        name={`variants[${index}].discount.value`}
                                                        component="div"
                                                        className="invalid-feedback"
                                                      />
                                                    </FormGroup>
                                                  </Col>
                                                  <Col md="4">
                                                    <FormGroup check>
                                                      <Field
                                                        type="checkbox"
                                                        name={`variants[${index}].discount.active`}
                                                        as={Input}
                                                      />
                                                      <Label check>Active</Label>
                                                    </FormGroup>
                                                  </Col>
                                                  <Col md="6">
                                                    <FormGroup>
                                                      <Label for={`variants[${index}].discount.startDate`}>Start Date</Label>
                                                      <Field
                                                        as={Input}
                                                        type="date"
                                                        name={`variants[${index}].discount.startDate`}
                                                        invalid={touched.variants?.[index]?.discount?.startDate && !!errors.variants?.[index]?.discount?.startDate}
                                                      />
                                                      <ErrorMessage
                                                        name={`variants[${index}].discount.startDate`}
                                                        component="div"
                                                        className="invalid-feedback"
                                                      />
                                                    </FormGroup>
                                                  </Col>
                                                  <Col md="6">
                                                    <FormGroup>
                                                      <Label for={`variants[${index}].discount.endDate`}>End Date</Label>
                                                      <Field
                                                        as={Input}
                                                        type="date"
                                                        name={`variants[${index}].discount.endDate`}
                                                        invalid={touched.variants?.[index]?.discount?.endDate && !!errors.variants?.[index]?.discount?.endDate}
                                                      />
                                                      <ErrorMessage
                                                        name={`variants[${index}].discount.endDate`}
                                                        component="div"
                                                        className="invalid-feedback"
                                                      />
                                                    </FormGroup>
                                                  </Col>
                                                </Row>
                                              </CardBody>
                                            </Card>
                                          </Col>
                                          <Col md="12">
                                            <Button
                                              color="danger"
                                              outline
                                              onClick={() => remove(index)}
                                              className="mt-2"
                                            >
                                              <FaTrash className="me-1" /> Remove Variant
                                            </Button>
                                          </Col>
                                        </Row>
                                      </CardBody>
                                    </Collapse>
                                  </Card>
                                ))
                              ) : (
                                <p className="text-muted">No variants added yet.</p>
                              )}
                              <Button
                                color="primary"
                                outline
                                onClick={() => {
                                  push({
                                    name: "",
                                    price: "",
                                    stock: "",
                                    weight: "",
                                    dimensions: { height: "", length: "", width: "" },
                                    sku: "",
                                    discount: {
                                      type: "",
                                      value: "",
                                      active: false,
                                      startDate: "",
                                      endDate: ""
                                    },
                                    images: []
                                  });
                                  setVariantOpen((prev) => [...prev, true]);
                                }}
                                className="mt-3"
                              >
                                <FaPlus className="me-1" /> Add Variant
                              </Button>
                              {errors.variants && typeof errors.variants === "string" && (
                                <div className="invalid-feedback d-block mt-2">{errors.variants}</div>
                              )}
                            </div>
                          )}
                        </FieldArray>
                      </CardBody>
                    </Card>

                    {/* Media Section */}
                    <Card className="mb-4 border-0 shadow-sm">
                      <CardHeader className="bg-light">
                        <h5 className="mb-0">Media</h5>
                      </CardHeader>
                      <CardBody>
                        <Row className="g-4">
                          <Col md="12">
                            <FormGroup>
                              <Label for="images">Product Images</Label>
                              <Dropzone
                                onDrop={(acceptedFiles) => handleImageDropChange(acceptedFiles, setFieldValue)}
                                accept="image/jpeg, image/png, image/jpg"
                                maxFiles={5}
                              >
                                {({ getRootProps, getInputProps, isDragActive }) => (
                                  <div
                                    {...getRootProps()}
                                    className={`dropzone p-4 border-2 rounded-3 text-center ${isDragActive ? "border-primary bg-light" : "border-dashed border-secondary"
                                      }`}
                                    style={{ cursor: "pointer" }}
                                  >
                                    <input {...getInputProps()} id="images" />
                                    <p className="mb-1 text-muted">
                                      Drag & drop images here or click to select
                                    </p>
                                    <p className="mb-0 text-secondary small">
                                      (Accepted: .jpeg, .png, .jpg | Max 5 files)
                                    </p>
                                  </div>
                                )}
                              </Dropzone>
                              <div className="d-flex flex-wrap mt-3 g-2">
                                {existingImages.map((image, index) => (
                                  <div
                                    key={`existing_${index}`}
                                    className="position-relative"
                                    style={{ width: 120, height: 120 }}
                                  >
                                    <img
                                      src={image}
                                      alt="preview"
                                      className="rounded img-fluid border shadow-sm"
                                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                    />
                                    <Button
                                      color="danger"
                                      size="sm"
                                      className="position-absolute top-0 end-0"
                                      onClick={() => handleRemoveImage(index, true, setFieldValue)}
                                    >
                                      ×
                                    </Button>
                                  </div>
                                ))}
                                {newImages.map((file, index) => (
                                  <div
                                    key={`new_${index}`}
                                    className="position-relative"
                                    style={{ width: 120, height: 120 }}
                                  >
                                    <img
                                      src={file.preview}
                                      alt="preview"
                                      className="rounded img-fluid border shadow-sm"
                                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                    />
                                    <Button
                                      color="danger"
                                      size="sm"
                                      className="position-absolute top-0 end-0"
                                      onClick={() => handleRemoveImage(index, false, setFieldValue)}
                                    >
                                      ×
                                    </Button>
                                  </div>
                                ))}
                              </div>
                              <ErrorMessage name="images" component="div" className="invalid-feedback d-block mt-2" />
                            </FormGroup>
                          </Col>
                          <Col md="12">
                            <FormGroup>
                              <Label for="video">Video (Optional)</Label>
                              <Dropzone
                                onDrop={(acceptedFiles) => handleVideoDropChange(acceptedFiles, setFieldValue)}
                                accept="video/mp4"
                                maxFiles={1}
                              >
                                {({ getRootProps, getInputProps, isDragActive }) => (
                                  <div
                                    {...getRootProps()}
                                    className={`dropzone p-4 border-2 rounded-3 text-center ${isDragActive ? "border-primary bg-light" : "border-dashed border-secondary"
                                      }`}
                                    style={{ cursor: "pointer" }}
                                  >
                                    <input {...getInputProps()} id="video" />
                                    <p className="mb-1 text-muted">
                                      Drag & drop a video here or click to select
                                    </p>
                                    <p className="mb-0 text-secondary small">(Accepted: .mp4 | Max 1 file)</p>
                                  </div>
                                )}
                              </Dropzone>
                              {(existingVideo || newVideo) && (
                                <div className="mt-3 position-relative" style={{ width: 200, height: 150 }}>
                                  <video
                                    src={newVideo ? newVideo.preview : existingVideo}
                                    controls
                                    className="rounded border shadow-sm"
                                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                  />
                                  <Button
                                    color="danger"
                                    size="sm"
                                    className="position-absolute top-0 end-0"
                                    onClick={() => handleRemoveVideo(setFieldValue)}
                                  >
                                    ×
                                  </Button>
                                </div>
                              )}
                              <ErrorMessage name="video" component="div" className="invalid-feedback d-block mt-2" />
                            </FormGroup>
                          </Col>
                        </Row>
                      </CardBody>
                    </Card>

                    {/* Form Actions */}
                    <div className="d-flex justify-content-end">
                      <Button
                        color="secondary"
                        outline
                        onClick={() => navigate("/products")}
                        disabled={loading}
                      >
                        Cancel
                      </Button>
                      <Button color="primary" type="submit" className="ms-2" disabled={loading}>
                        {loading ? (
                          <>
                            <Spinner size="sm" className="me-2" /> Saving...
                          </>
                        ) : (
                          "Update Product"
                        )}
                      </Button>
                    </div>
                  </Form>
                )}
              </Formik>
            ) : (
              <div className="text-center">
                <Spinner color="primary" />
              </div>
            )}
          </CardBody>
        </PreviewCard>
      </div>
    </Content>
  );
};

export default EditProductPage;